/**
 * Group Wiki Store - Zustand
 * Manages wiki articles for prep groups
 */

import { create } from 'zustand';
import { apiGet, apiPost, apiPut, apiDelete } from '@/services/api-client';
import { useAuthStore } from './auth-store';
import { logKnowledgeAction } from '@/lib/activity-logger';

export interface WikiArticle {
  id: string;
  groupId: string;
  title: string;
  content: string; // Markdown
  tags: string[];
  isProtected: boolean;
  authorId: string;
  authorName: string;
  createdAt: string;
  updatedAt: string;
}

interface GroupWikiState {
  articlesByGroup: Record<string, WikiArticle[]>;
  loading: boolean;

  // Actions
  fetchArticles: (groupId: string) => Promise<void>;
  createArticle: (groupId: string, data: {
    title: string;
    content: string;
    tags: string[];
    isProtected: boolean;
  }) => Promise<WikiArticle | null>;
  updateArticle: (groupId: string, articleId: string, data: {
    title?: string;
    content?: string;
    tags?: string[];
    isProtected?: boolean;
  }) => Promise<boolean>;
  deleteArticle: (groupId: string, articleId: string) => Promise<boolean>;
}

export const useGroupWikiStore = create<GroupWikiState>((set, _get) => ({
  articlesByGroup: {},
  loading: false,

  fetchArticles: async (groupId: string) => {
    set({ loading: true });
    try {
      const result = await apiGet<WikiArticle[]>(`/groups/${groupId}/wiki`);
      if (result.success && result.data) {
        set(state => ({
          articlesByGroup: {
            ...state.articlesByGroup,
            [groupId]: result.data!,
          },
        }));
      }
    } catch (error) {
      console.error('Failed to fetch wiki articles', error);
    } finally {
      set({ loading: false });
    }
  },

  createArticle: async (groupId: string, data) => {
    const user = useAuthStore.getState().user;
    
    // Premium check: Free users cannot create wiki articles
    if (user?.subscriptionTier !== 'premium') {
      throw new Error('Endast Premium-användare kan skapa wiki-artiklar');
    }

    // Premium users: Check daily limit (30 articles/day)
    const countResult = await apiGet<{ todayCount: number }>(`/groups/${groupId}/wiki/count-today`);
    if (countResult.success && countResult.data) {
      const { todayCount } = countResult.data;
      if (todayCount >= 30) {
        throw new Error('Du har nått dagens gräns på 30 wiki-artiklar');
      }
    }

    try {
      const result = await apiPost<WikiArticle>(`/groups/${groupId}/wiki`, data);
      if (result.success && result.data) {
        logKnowledgeAction('EDIT_WIKI', result.data.id, { groupId, title: data.title });
        set(state => ({
          articlesByGroup: {
            ...state.articlesByGroup,
            [groupId]: [result.data!, ...(state.articlesByGroup[groupId] || [])],
          },
        }));
        return result.data;
      }
      return null;
    } catch (error) {
      console.error('Failed to create wiki article', error);
      throw error;
    }
  },

  updateArticle: async (groupId: string, articleId: string, data) => {
    const user = useAuthStore.getState().user;
    
    // Premium check: Free users cannot update wiki articles
    if (user?.subscriptionTier !== 'premium') {
      throw new Error('Endast Premium-användare kan redigera wiki-artiklar');
    }

    try {
      const result = await apiPut<WikiArticle>(`/groups/${groupId}/wiki/${articleId}/update`, data);
      if (result.success && result.data) {
        logKnowledgeAction('EDIT_WIKI', articleId, { groupId });
        set(state => ({
          articlesByGroup: {
            ...state.articlesByGroup,
            [groupId]: (state.articlesByGroup[groupId] || []).map(a =>
              a.id === articleId ? result.data! : a
            ),
          },
        }));
        return true;
      }
      return false;
    } catch (error) {
      console.error('Failed to update wiki article', error);
      throw error;
    }
  },

  deleteArticle: async (groupId: string, articleId: string) => {
    const user = useAuthStore.getState().user;
    
    // Premium check: Free users cannot delete wiki articles
    if (user?.subscriptionTier !== 'premium') {
      throw new Error('Endast Premium-användare kan radera wiki-artiklar');
    }

    try {
      const result = await apiDelete(`/groups/${groupId}/wiki/${articleId}/delete`);
      if (result.success) {
        logKnowledgeAction('EDIT_WIKI', articleId, { groupId, deleted: true });
        set(state => ({
          articlesByGroup: {
            ...state.articlesByGroup,
            [groupId]: (state.articlesByGroup[groupId] || []).filter(a => a.id !== articleId),
          },
        }));
        return true;
      }
      return false;
    } catch (error) {
      console.error('Failed to delete wiki article', error);
      throw error;
    }
  },
}));
